/**
 * 2D skeleton overlay for the original camera picture.
 * Points are already upright pixels: origin top-left, x right, y down.
 * The browser applies the GoPro display matrix, so these points are not rotated again.
 */

import { chainsForJoints } from './trialLiveLayout';

/** Matplotlib tab10, the palette the baked overlay was drawn with. */
export const MATPLOTLIB_TAB10 = [
  [31, 119, 180],
  [255, 127, 14],
  [44, 160, 44],
  [214, 39, 40],
  [148, 103, 189],
  [140, 86, 75],
  [227, 119, 194],
  [127, 127, 127],
  [188, 189, 34],
  [23, 190, 207],
];

export function matplotlibTab10Css(chainIndex) {
  const n = MATPLOTLIB_TAB10.length;
  const raw = Number(chainIndex);
  const index = Number.isFinite(raw) ? raw : 0;
  const slot = ((index % n) + n) % n;
  const [r, g, b] = MATPLOTLIB_TAB10[slot];
  return `rgb(${r}, ${g}, ${b})`;
}

export function chainIndexForEdge(joints, edge, chains) {
  if (!Array.isArray(joints) || !Array.isArray(edge) || edge.length < 2) return -1;
  const a = joints[edge[0]];
  const b = joints[edge[1]];
  const list = Array.isArray(chains) ? chains : [];
  for (let c = 0; c < list.length; c += 1) {
    const chain = list[c];
    if (!Array.isArray(chain)) continue;
    for (let i = 0; i < chain.length - 1; i += 1) {
      const left = chain[i];
      const right = chain[i + 1];
      if ((left === a && right === b) || (left === b && right === a)) return c;
    }
  }
  return -1;
}

export function edgeStrokeColor(joints, edge, chains) {
  const index = chainIndexForEdge(joints, edge, chains);
  if (index < 0) return 'rgb(127, 127, 127)';
  return matplotlibTab10Css(index);
}

function finiteDimension(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function normalizeEdges(rawEdges, jointCount) {
  if (!Array.isArray(rawEdges)) return [];
  return rawEdges
    .filter((pair) => Array.isArray(pair) && pair.length >= 2)
    .map((pair) => [Number(pair[0]), Number(pair[1])])
    .filter((pair) => pair.every((n) => Number.isInteger(n) && n >= 0 && n < jointCount) && pair[0] !== pair[1]);
}

function normalizeTrack(cam) {
  if (!cam || typeof cam !== 'object' || !cam.name) return null;
  const width = finiteDimension(cam.width);
  const height = finiteDimension(cam.height);
  const fps = finiteDimension(cam.fps);
  if (!width || !height || !fps) return null;
  return {
    name: String(cam.name),
    width,
    height,
    fps,
    xy: Array.isArray(cam.xy) ? cam.xy : [],
  };
}

export function normalizeReprojection(raw) {
  if (!raw || typeof raw !== 'object') {
    throw new Error('reprojection missing');
  }
  const joints = Array.isArray(raw.joints) ? raw.joints.map(String) : null;
  if (!joints || joints.length < 1) {
    throw new Error('reprojection joints missing');
  }
  const cameras = (Array.isArray(raw.cameras) ? raw.cameras : [])
    .map(normalizeTrack)
    .filter(Boolean);
  return {
    trialId: raw.trial_id != null ? String(raw.trial_id) : (raw.trialId != null ? String(raw.trialId) : ''),
    joints,
    edges: normalizeEdges(raw.edges, joints.length),
    cameras,
  };
}

export function trackForCamera(reprojection, camera) {
  if (!reprojection || !camera || !camera.name || !Array.isArray(reprojection.cameras)) return null;
  return reprojection.cameras.find((cam) => cam.name === camera.name) || null;
}

export function finitePoint(value) {
  if (!Array.isArray(value) || value.length < 2) return null;
  const x = Number(value[0]);
  const y = Number(value[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return [x, y];
}

/** Bones whose endpoints are both finite. A null projection is not drawn. */
export function overlaySegments(framePoints, edges) {
  if (!Array.isArray(framePoints) || !Array.isArray(edges)) return [];
  const segments = [];
  edges.forEach((edge) => {
    if (!Array.isArray(edge) || edge.length < 2) return;
    const a = finitePoint(framePoints[edge[0]]);
    const b = finitePoint(framePoints[edge[1]]);
    if (!a || !b) return;
    segments.push({ a, b, edge });
  });
  return segments;
}

/**
 * Where the upright picture sits inside the video element.
 * The player uses object-fit, so the image is not the grid cell: contain
 * leaves black bars, and the skeleton must use this rectangle.
 */
export function pictureFit(boxWidth, boxHeight, imageWidth, imageHeight, fit = 'cover') {
  if (!(boxWidth > 0) || !(boxHeight > 0) || !(imageWidth > 0) || !(imageHeight > 0)) return null;
  if (fit === 'fill') {
    return {
      scaleX: boxWidth / imageWidth,
      scaleY: boxHeight / imageHeight,
      offsetX: 0,
      offsetY: 0,
      displayedWidth: boxWidth,
      displayedHeight: boxHeight,
    };
  }
  const scale = fit === 'contain'
    ? Math.min(boxWidth / imageWidth, boxHeight / imageHeight)
    : Math.max(boxWidth / imageWidth, boxHeight / imageHeight);
  const displayedWidth = imageWidth * scale;
  const displayedHeight = imageHeight * scale;
  return {
    scaleX: scale,
    scaleY: scale,
    offsetX: (boxWidth - displayedWidth) / 2,
    offsetY: (boxHeight - displayedHeight) / 2,
    displayedWidth,
    displayedHeight,
  };
}

/** About 2 CSS px when the picture is shown at half its pixel width. */
export function overlayStrokeWidth(displayedWidth, imageWidth) {
  if (!(displayedWidth > 0) || !(imageWidth > 0)) return 2;
  return 2 * (displayedWidth / (imageWidth * 0.5));
}

export function paintCameraOverlay(ctx, options) {
  const {
    boxWidth,
    boxHeight,
    imageWidth,
    imageHeight,
    framePoints,
    edges,
    joints,
    dpr = 1,
    fit = 'cover',
  } = options || {};
  const ratio = Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.clearRect(0, 0, boxWidth, boxHeight);
  const placed = pictureFit(boxWidth, boxHeight, imageWidth, imageHeight, fit);
  if (!placed) return;
  const chains = chainsForJoints(joints);
  const lineWidth = overlayStrokeWidth(placed.displayedWidth, imageWidth);
  const groups = new Map();
  overlaySegments(framePoints, edges).forEach((segment) => {
    const color = edgeStrokeColor(joints, segment.edge, chains);
    const line = [
      placed.offsetX + segment.a[0] * placed.scaleX,
      placed.offsetY + segment.a[1] * placed.scaleY,
      placed.offsetX + segment.b[0] * placed.scaleX,
      placed.offsetY + segment.b[1] * placed.scaleY,
    ];
    const bucket = groups.get(color);
    if (bucket) bucket.push(line);
    else groups.set(color, [line]);
  });
  groups.forEach((lines, color) => {
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    lines.forEach((line) => {
      ctx.moveTo(line[0], line[1]);
      ctx.lineTo(line[2], line[3]);
    });
    ctx.stroke();
  });
}
